import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Product, productService, QUERY_KEYS } from '@/services/productService';
import { ProductDetailLayout } from '@/components/products/ProductDetailLayout';
import ProductDetailSidebar from '@/components/products/ProductDetailSidebar';
import { ProductDetailTabs } from '@/components/products/ProductDetailTabs';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Printer, ChevronLeft, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmationModal, useConfirmationModal } from '@/components/ui/ConfirmationModal';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { PermissionGuard } from '@/components/common/PermissionGuard';

export const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const productId = id ? Number(id) : 0;
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // i18n Step 4a: Get translations hook
  const { getPageLabels, getPageNotification } = useProductDetailPageTranslations();

  // Confirmation modal for archive action
  const archiveModal = useConfirmationModal();

  // Get user permissions from auth context
  const { checkPermission } = useAuth();
  const canArchive = checkPermission ? checkPermission('product.delete') : true;

  // Use React Query to fetch the product with retry logic
  const {
    data: product,
    isLoading: loading,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      if (!productId) {
        throw new Error(getPageLabels.errors.productIdMissing);
      }

      try {
        // Fetch product data
        const productData = await productService.getProduct(productId);

        if (!productData) {
          throw new Error(getPageLabels.errors.emptyData);
        }

        // Fetch additional data
        const assetsData = await productService.getProductAssets(productId);

        // Build complete product with assets
        const productWithAssets: Product = {
          ...productData,
          assets: Array.isArray(assetsData) && assetsData.length > 0 ? assetsData : [],
        };

        // i18n Step 4b: Set page title with translation
        document.title = `${productWithAssets.name || 'Product'} - ${getPageLabels.title}`;

        return productWithAssets;
      } catch (err: any) {
        if (err.response?.status === 404) {
          throw new Error(getPageLabels.errors.productNotFound);
        }
        throw new Error(getPageLabels.errors.loadFailed);
      }
    },
    retry: (failureCount, error: any) => {
      // Only retry a few times and only for network errors or if we think
      // the product might still be propagating through the database
      const isNotFound = error.response?.status === 404;

      // Retry up to 3 times for any error except a confirmed 404
      return !isNotFound && failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    refetchOnWindowFocus: false,
  });

  // Use React Query to fetch prices with standardized query key
  const { data: prices, isLoading: isPricesLoading } = useQuery({
    queryKey: QUERY_KEYS.PRODUCT_PRICES(productId),
    queryFn: () => productService.getPrices(productId),
    enabled: !!productId,
    refetchOnWindowFocus: false,
  });

  // Use React Query Client to invalidate queries
  const queryClient = useQueryClient();

  // Clean up on unmount
  useEffect(() => {
    // Force refetch product data and related queries when component mounts
    // This ensures we have fresh data when returning from edit form
    if (productId) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(productId) });
      queryClient.invalidateQueries({ queryKey: ['familyAttributeGroups'] });
      queryClient.invalidateQueries({ queryKey: ['attributes'] });
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.PRODUCT(productId) });
    }

    return () => {
      // i18n Step 4c: Reset page title to translated version
      document.title = getPageLabels.title;
    };
  }, [productId, getPageLabels.title, queryClient]);

  // Handle product archiving (archived products are automatically deleted after some time)
  const handleArchive = () => {
    archiveModal.openModal();
  };

  // i18n Step 4d: Handle PDF download with translated notifications
  const handlePrintPdf = async () => {
    if (!id || !product) return;

    try {
      setSaving(true);
      await productService.downloadProductPdf(Number(id), product.name);
      toast.success(getPageNotification('pdfDownloadSuccess'));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || getPageNotification('pdfDownloadFailed');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // i18n Step 4e: Archive function with translated notifications
  const performArchive = async () => {
    if (!id) return;

    try {
      setSaving(true);
      await productService.deleteProduct(Number(id));
      toast.success(getPageNotification('archiveSuccess'));

      // Navigate immediately after successful archive operation
      // Don't wait for any other operations that might fail
      navigate('/app/products');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || getPageNotification('archiveFailed');
      toast.error(errorMessage);
      throw err; // Re-throw to keep modal open on error
    } finally {
      setSaving(false);
    }
  };

  // Handle product update from description component
  const handleProductUpdate = async (_updatedProduct: Product) => {
    // Refetch product data to ensure we have the latest
    refetch();
  };

  const handleRetry = () => {
    refetch();
  };

  // i18n Step 4f: Loading state with translated content
  if (loading) {
    return (
      <div className="mx-auto w-full px-6 py-8">
        <div className="mb-6 flex items-center gap-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="size-5" />
          <Skeleton className="h-5 w-24" />
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div>
            <Skeleton className="mb-2 h-10 w-48" />
            <Skeleton className="h-6 w-36" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className="lg:col-span-3">
            <Skeleton className="mb-4 h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
          <div className="lg:col-span-1">
            <Skeleton className="h-[450px] w-full" />
          </div>
        </div>
      </div>
    );
  }

  // i18n Step 4g: Error state with translated messages
  if (isError || !product) {
    const errorMessage =
      queryError instanceof Error ? queryError.message : getPageLabels.errors.productNotFound;

    return (
      <div className="mx-auto w-full px-6 py-8">
        <div className="mb-6">
          <Button variant="outline" className="mb-4" onClick={() => navigate('/app/products')}>
            <ChevronLeft className="mr-2 size-4" />
            {getPageLabels.errors.backToProducts}
          </Button>

          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="size-4" />
            <AlertTitle>{getPageLabels.errors.genericError}</AlertTitle>
            <AlertDescription>
              {errorMessage}
              <Button variant="link" className="h-auto p-0 text-sm" onClick={handleRetry}>
                {getPageLabels.errors.tryAgain}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // i18n Step 4h: Main content with translated strings
  return (
    <div className="mx-auto w-full px-6 py-8">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/app/products">{getPageLabels.breadcrumb.products}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink>
              {product?.name || getPageLabels.breadcrumb.productDetails}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header with actions */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-foreground">{product?.name}</h1>
            <div className="ml-2">
              {product?.is_active ? (
                <Badge variant="success">{getPageLabels.header.statusBadges.active}</Badge>
              ) : (
                <Badge variant="destructive">{getPageLabels.header.statusBadges.inactive}</Badge>
              )}
            </div>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {getPageLabels.header.skuLabel} {product?.sku}
          </p>
        </div>

        <div className="mt-4 flex gap-2 sm:mt-0">
          {/* Main action buttons */}
          <PermissionGuard permission="product.view">
            <Button onClick={handlePrintPdf} disabled={saving} variant="outline" className="gap-1">
              <Printer className="mr-1 size-4" />
              {getPageLabels.header.buttons.printPdf}
            </Button>
          </PermissionGuard>

          {canArchive && (
            <Button
              onClick={handleArchive}
              disabled={saving}
              variant="outline"
              className="gap-1 border-amber-200 text-amber-600 hover:bg-amber-50"
            >
              <Archive className="mr-1 size-4" />
              {getPageLabels.header.buttons.archive}
            </Button>
          )}
        </div>
      </div>

      {/* Main layout */}
      <ProductDetailLayout
        content={
          <ProductDetailTabs
            product={product}
            onProductUpdate={handleProductUpdate}
            prices={prices || []}
            isPricesLoading={isPricesLoading}
          />
        }
        sidebar={
          <ProductDetailSidebar
            product={product as any}
            prices={prices || []}
            isPricesLoading={isPricesLoading}
          />
        }
      />

      {/* i18n Step 4i: Archive Confirmation Modal with translated content */}
      <ConfirmationModal
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.closeModal}
        onConfirm={() => archiveModal.handleConfirm(performArchive)}
        title={getPageLabels.archiveModal.title}
        description={getPageLabels.archiveModal.description}
        confirmText={getPageLabels.archiveModal.confirmText}
        cancelText={getPageLabels.archiveModal.cancelText}
        variant="warning"
        isLoading={archiveModal.isLoading}
        metadata={
          product
            ? [
                {
                  label: getPageLabels.archiveModal.metadata.sku,
                  value: product.sku,
                },
                {
                  label: getPageLabels.archiveModal.metadata.name,
                  value: product.name,
                },
                {
                  label: getPageLabels.archiveModal.metadata.status,
                  value: product.is_active
                    ? getPageLabels.header.statusBadges.active
                    : getPageLabels.header.statusBadges.inactive,
                },
              ]
            : undefined
        }
        warningText={getPageLabels.archiveModal.warningText}
      />
    </div>
  );
};
